import { IconPlugConnectedX, IconServerOff } from "@tabler/icons-react";

import { ApiError } from "../api/client";
import { SUPPORT_CHANNEL } from "../config";

/**
 * True when an error means "there is no truth to show", not "this failed".
 *
 * Args:
 *     error: Anything thrown by a query.
 *
 * Returns:
 *     True for `503 not_connected` and `502 object_store_unavailable`.
 */
export function isStorageProblem(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    (error.isNotConnected || error.isStorageUnavailable)
  );
}

interface StorageProblemProps {
  error: ApiError;
  onRetry?: () => void;
}

interface Copy {
  title: string;
  lead: string;
  checklistTitle: string;
  checklist: [string, string][];
}

const NOT_CONNECTED: Copy = {
  title: "The console has not been told where files are kept",
  lead:
    "It can still show you the record of every dataset — who the client is, " +
    "what they allowed and what the bar is — because that lives in its own " +
    "database. What it cannot do is take a file or hand one out. This is a " +
    "setting on the console's side, not something you did and not something " +
    "you can fix — the platform team can, in a minute. Nothing of anybody's " +
    "is lost, and no client's file is at risk.",
  checklistTitle: "For the platform team",
  checklist: [
    ["S3_ENDPOINT", "where secure storage answers."],
    [
      "S3_QUARANTINE_BUCKET",
      "the holding area a file lands in first, and which no reading route can see.",
    ],
    ["S3_VAULT_BUCKET", "where a file goes once the console has read it."],
    [
      "S3_REPLICA_BUCKET",
      "optional — leave it empty and every screen says there is no second copy, " +
        "which is the honest state rather than a blank that reads as a promise.",
    ],
    [
      "the four access keys",
      "one that may only write into the holding area, one that reads it and " +
        "writes the vault, one that reads the vault, one that may erase. They " +
        "are not interchangeable and none of them is ever in this page.",
    ],
  ],
};

const UNAVAILABLE: Copy = {
  title: "Secure storage did not answer",
  lead:
    "The console knows where files are kept but could not reach it just now. " +
    "Nothing was written and nothing is lost: no file changed state, no " +
    "second copy was un-stamped, and no upload was thrown away for a deadline " +
    "that passed while this lasted. The catalogue below is the record, and it " +
    "is unaffected. If this lasts more than a few minutes it is worth saying " +
    "so.",
  checklistTitle: "For the platform team",
  checklist: [
    ["Network", "can the console reach the storage endpoint at all?"],
    [
      "Credentials",
      "are the access keys still valid, and does each still have the one role it was given?",
    ],
    [
      "The buckets",
      "do all three still exist, in the regions the console was told about?",
    ],
  ],
};

/**
 * What a person sees when there is no measured truth to show.
 *
 * It leads with the two things they need — this is not your fault, and here is
 * who fixes it — and keeps the settings behind a fold. A wall of configuration
 * is the deployer's screen, and the audience here is a GTM person
 * (`docs/CONVENTIONS.md`). There is no cached catalogue behind this panel and
 * no last-known-good: a screen with invented files would be worse than no
 * screen.
 *
 * Args:
 *     error: The API error that caused it.
 *     onRetry: Optional retry action.
 *
 * Returns:
 *     A full-width explanatory panel.
 */
export function StorageProblem({
  error,
  onRetry,
}: StorageProblemProps): JSX.Element {
  const copy = error.isNotConnected ? NOT_CONNECTED : UNAVAILABLE;
  const Icon = error.isNotConnected ? IconPlugConnectedX : IconServerOff;
  const details = Object.entries(error.details ?? {});

  return (
    <section className="problem" role="alert" aria-labelledby="problem-title">
      <div className="problem__head">
        <Icon size={20} aria-hidden="true" />
        <h2 className="problem__title" id="problem-title">
          {copy.title}
        </h2>
      </div>

      <p className="problem__lead">{copy.lead}</p>

      <p className="problem__lead">Who to tell: {SUPPORT_CHANNEL}.</p>

      {onRetry ? (
        <button type="button" className="btn" onClick={onRetry}>
          Check again
        </button>
      ) : null}

      <details className="problem__details-block">
        <summary>{copy.checklistTitle}</summary>

        <div className="problem__quote">
          <span className="problem__quote-label">The server said</span>
          <p className="problem__quote-text">{error.message}</p>
          <p className="problem__quote-code mono">
            HTTP {error.status} · {error.code}
          </p>
          {details.length > 0 ? (
            <dl className="problem__details mono">
              {details.map(([key, value]) => (
                <div key={key} className="problem__detail">
                  <dt>{key}</dt>
                  <dd>
                    {typeof value === "string" ? value : JSON.stringify(value)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>

        <ul className="problem__list">
          {copy.checklist.map(([name, explanation]) => (
            <li key={name} className="problem__item">
              <span className="mono problem__key">{name}</span>
              <span className="problem__explanation">{explanation}</span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
